import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import SiteFooter from "@/components/SiteFooter";
import LangSwitcher from "@/components/LangSwitcher";
import ExitIntentOffer from "@/components/ExitIntentOffer";
import { useI18n } from "@/i18n/useI18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Zap, ArrowRight, Check, X, Sparkles, ShieldCheck, Loader2,
  Twitter, Facebook, Linkedin, Instagram,
} from "lucide-react";

const handleLogin = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function SeoLanding({ pageType }) {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const siteOrigin = typeof window !== "undefined" ? window.location.origin : "https://autogridsocial.com";
  const enPathClean = `/${pageType}/${slug}`;
  const esPathClean = `/es/${pageType}/${slug}`;

  useEffect(() => {
    setPage(null);
    setNotFound(false);
    const q = lang === "es" ? "?lang=es" : "";
    api.get(`/seo/page/${pageType}/${slug}${q}`)
      .then((r) => setPage(r.data))
      .catch(() => setNotFound(true));
  }, [pageType, slug, lang]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center gap-4">
        <h1 className="text-2xl font-display font-bold">Page not found</h1>
        <Link to="/" className="text-primary underline">Back to AutoGridSocial</Link>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const canonical = siteOrigin + (lang === "es" ? esPathClean : enPathClean);
  const softwareLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "AutoGridSocial",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description: page.meta_description,
    offers: [
      { "@type": "Offer", price: "29", priceCurrency: "USD", name: "Creator" },
      { "@type": "Offer", price: "79", priceCurrency: "USD", name: "E-commerce Growth" },
      { "@type": "Offer", price: "199", priceCurrency: "USD", name: "Agency" },
    ],
    aggregateRating: { "@type": "AggregateRating", ratingValue: "4.8", ratingCount: "126" },
  };
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: (page.faq_items || []).map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: origin + "/" },
      { "@type": "ListItem", position: 2, name: page.badge_label || page.audience || page.competitor, item: canonical },
    ],
  };
  const howtoLd = (page.howto_steps && page.howto_steps.length) ? {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: page.headline,
    description: page.meta_description,
    step: page.howto_steps.map((s, i) => ({
      "@type": "HowToStep", position: i + 1, name: s.name, text: s.text,
    })),
  } : null;
  const badge = page.badge_label
    ? page.badge_label
    : (pageType === "vs" ? `vs ${page.competitor}` : `For ${page.audience}`);
  const painHeader = page.pain_header
    ? page.pain_header
    : (pageType === "vs"
        ? `${t("seo.leaves_a", "Where")} ${page.competitor} ${t("seo.leaves_b", "leaves you stuck")}`
        : t("seo.sound_familiar", "Sound familiar?"));

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="seo-landing">
      <Helmet>
        <title>{page.title}</title>
        <meta name="description" content={page.meta_description} />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="en" href={siteOrigin + enPathClean} />
        <link rel="alternate" hrefLang="es" href={siteOrigin + esPathClean} />
        <link rel="alternate" hrefLang="x-default" href={siteOrigin + enPathClean} />
        <meta property="og:title" content={page.title} />
        <meta property="og:description" content={page.meta_description} />
        <meta property="og:image" content={page.banner_image_url} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(softwareLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
        {howtoLd && <script type="application/ld+json">{JSON.stringify(howtoLd)}</script>}
      </Helmet>

      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="seo-nav-home">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
              <Zap className="h-5 w-5 text-white" />
            </div>
            <span className="font-display font-bold text-lg tracking-tight">AutoGridSocial</span>
          </Link>
          <div className="flex items-center gap-3">
            <LangSwitcher />
            <Button data-testid="seo-nav-cta" onClick={handleLogin} className="bg-primary hover:bg-primary/90 text-white text-sm">
              {t("seo.cta_trial", "Get started")}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div className="absolute inset-0 grid-noise pointer-events-none" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[420px] w-[820px] rounded-full bg-primary/10 blur-[120px] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-14">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fadeUp">
              <Badge className="mb-5 bg-secondary text-foreground border-border font-mono text-xs">
                <Sparkles className="h-3.5 w-3.5 mr-1.5 text-accent" />
                {badge}
              </Badge>
              <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.08] mb-5">
                {page.headline}
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground max-w-xl mb-7 leading-relaxed">
                {page.subheadline}
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <Button data-testid="seo-hero-cta" onClick={handleLogin} size="lg"
                  className="bg-primary hover:bg-primary/90 text-white h-12 px-7 text-base group">
                  {t("seo.cta_trial", "Get started")}
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
                <span className="text-sm text-muted-foreground">{t("seo.trial_micro", "Plans from $29/mo · cancel anytime")}</span>
              </div>
              <div className="flex items-center gap-4 mt-8 text-muted-foreground">
                {[Twitter, Facebook, Linkedin, Instagram].map((Icon, i) => (
                  <Icon key={i} className="h-5 w-5" />
                ))}
              </div>
            </div>
            <div className="animate-fadeUp" style={{ animationDelay: "0.1s" }}>
              <div className="rounded-2xl overflow-hidden border border-border relative">
                <img src={page.banner_image_url} alt={page.audience || page.competitor}
                  className="w-full h-72 object-cover" data-testid="seo-banner-image" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pain points */}
      <section className="border-b border-border/60 bg-card/30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight text-center mb-10">
            {painHeader}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(page.pain_points || []).map((p, i) => (
              <div key={i} className="rounded-xl border border-rose-500/25 bg-rose-500/5 p-5 flex items-start gap-3"
                data-testid={`seo-pain-${i}`}>
                <span className="h-6 w-6 rounded-full bg-rose-500/15 flex items-center justify-center shrink-0 mt-0.5">
                  <X className="h-3.5 w-3.5 text-rose-400" />
                </span>
                <p className="text-sm text-muted-foreground leading-relaxed">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-b border-border/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-primary/15 text-primary border-primary/30">{t("seo.why", "Why AutoGridSocial")}</Badge>
            <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight">
              {t("seo.concrete", "Concrete posts, on autopilot")}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(page.features_highlight || []).map((f, i) => (
              <div key={i} className="rounded-xl border border-border bg-card p-5 flex items-start gap-3 card-hover"
                data-testid={`seo-feature-${i}`}>
                <span className="h-6 w-6 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="h-3.5 w-3.5 text-emerald-400" />
                </span>
                <p className="text-sm text-foreground/90 leading-relaxed">{f}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison table (AEO-friendly, for vs pages) */}
      {page.comparison && page.comparison.length > 0 && (
        <section className="border-b border-border/60" data-testid="seo-comparison">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight text-center mb-10">
              {`AutoGridSocial vs ${page.competitor}${t("seo.at_glance", ", at a glance")}`}
            </h2>
            <div className="rounded-xl border border-border overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-secondary/40 text-left">
                    <th className="px-4 py-3 font-medium">{t("seo.feature", "Feature")}</th>
                    <th className="px-4 py-3 font-medium text-primary">AutoGridSocial</th>
                    <th className="px-4 py-3 font-medium text-muted-foreground">{page.competitor}</th>
                  </tr>
                </thead>
                <tbody>
                  {page.comparison.map((row, i) => (
                    <tr key={i} className="border-t border-border" data-testid={`seo-compare-row-${i}`}>
                      <td className="px-4 py-3 text-foreground/90">{row.feature}</td>
                      <td className="px-4 py-3 font-medium">{row.us}</td>
                      <td className="px-4 py-3 text-muted-foreground">{row.them}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* How it works (HowTo, for feature pages) */}
      {page.howto_steps && page.howto_steps.length > 0 && (
        <section className="border-b border-border/60 bg-card/30" data-testid="seo-howto">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight text-center mb-10">
              {t("seo.how", "How it works")}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {page.howto_steps.map((s, i) => (
                <div key={i} className="rounded-xl border border-border bg-card p-5" data-testid={`seo-howto-step-${i}`}>
                  <div className="h-8 w-8 rounded-full bg-primary text-white font-display font-bold flex items-center justify-center mb-3">{i + 1}</div>
                  <h3 className="font-semibold text-sm mb-1.5">{s.name}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="border-b border-border/60 bg-card/30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight text-center mb-10">
            {t("seo.faq", "Frequently asked questions")}
          </h2>
          <div className="space-y-4">
            {(page.faq_items || []).map((f, i) => (
              <div key={i} className="rounded-xl border border-border bg-card p-5" data-testid={`seo-faq-${i}`}>
                <h3 className="font-semibold text-sm mb-2 flex items-start gap-2">
                  <ShieldCheck className="h-4 w-4 text-primary mt-0.5 shrink-0" /> {f.q}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed pl-6">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-border/60">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="text-3xl font-display font-bold tracking-tight mb-4">
            {t("seo.cta_title", "Put your posting on autopilot today")}
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            {t("seo.cta_sub", "Plans from $29/mo — Creator for creators, E-commerce Growth for Shopify stores, Agency for teams. Cancel anytime in one click.")}
          </p>
          <Button data-testid="seo-footer-cta" onClick={handleLogin} size="lg"
            className="bg-primary hover:bg-primary/90 text-white h-12 px-8 text-base group">
            {t("seo.cta_trial_full", "Get started")}
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Button>
        </div>
      </section>

      <SiteFooter />
      <ExitIntentOffer />
    </div>
  );
}
